import type { Word } from '@goldcast/player-config/src/types/transcript-types';
import type { ClipDeletes } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { roundToNDecimalPlaces } from '@goldcast/player-config/src/utils/mathUtils';
import { approxEquals, approxGTE, approxLTE } from '@goldcast/player-config/src/utils/compareUtils';
import {
  TRANSCRIPT_PAUSE_THRESHOLDS,
  SPEAKER_PACE_ANALYSIS,
  SPEAKER_PACE_TARGETS,
  MIN_OPTIMIZATION_PERCENT,
  PAUSE_PRESERVE_BEFORE_RATIO
} from './constants';
import { TranscriptPauseEntry, TranscriptPauseType, TranscriptParagraph, SpeakerPace, PauseContext } from './types';
import { addDecimalIfNeeded } from '@/libs/utils';

export function getPauseType(pauseDuration: number): TranscriptPauseType | undefined {
  if (pauseDuration >= TRANSCRIPT_PAUSE_THRESHOLDS.deadSpace) {
    return TranscriptPauseType.DeadSpace;
  }
  if (pauseDuration >= TRANSCRIPT_PAUSE_THRESHOLDS.longPause) {
    return TranscriptPauseType.LongPause;
  }
  if (pauseDuration >= TRANSCRIPT_PAUSE_THRESHOLDS.microPause) {
    return TranscriptPauseType.MicroPause;
  }
  return undefined;
}

export function getPauseLabel(pauseType: TranscriptPauseType): string {
  switch (pauseType) {
    case TranscriptPauseType.DeadSpace:
      return 'Dead space';
    case TranscriptPauseType.LongPause:
      return 'Long pause';
    case TranscriptPauseType.MicroPause:
      return 'Micro pause';
  }
}

type PauseTargetType = TranscriptPauseType | 'normalPause';

function getPauseTargetType(pauseType: TranscriptPauseType, context?: PauseContext): PauseTargetType {
  if (!context) {
    return pauseType;
  }

  if (context.isParagraphBoundary) {
    return TranscriptPauseType.DeadSpace;
  }

  if (context.hasSentenceEndingPunctuation) {
    return 'normalPause';
  }

  if (context.hasClauseEndingPunctuation) {
    return 'normalPause';
  }

  return TranscriptPauseType.MicroPause;
}

export function calculateSpeakerPace(
  allWords: Array<{ paragraphIndex: number; wordIndex: number; word: Word }>
): SpeakerPace {
  if (allWords.length < 2) {
    return 'normal';
  }

  const gaps: number[] = [];
  for (let i = 0; i < allWords.length - 1; i++) {
    const currentWord = allWords[i].word;
    const nextWord = allWords[i + 1].word;
    if (allWords[i].paragraphIndex === allWords[i + 1].paragraphIndex) {
      const gap = nextWord.start_time - currentWord.end_time;
      if (gap > 0 && gap < SPEAKER_PACE_ANALYSIS.maxCadenceGap) {
        gaps.push(gap);
      }
    }
  }

  if (gaps.length === 0) {
    return 'normal';
  }

  const avgGap = gaps.reduce((a, b) => a + b, 0) / gaps.length;

  if (avgGap > SPEAKER_PACE_ANALYSIS.slowAvgGapThreshold) {
    return 'slow';
  }
  if (avgGap < SPEAKER_PACE_ANALYSIS.fastAvgGapThreshold) {
    return 'fast';
  }
  return 'normal';
}

export function getRecommendedPauseDuration(
  pauseDuration: number,
  pauseType: TranscriptPauseType,
  speakerPace: SpeakerPace,
  context?: PauseContext
): number | null {
  const targets = SPEAKER_PACE_TARGETS[speakerPace];
  const targetType = getPauseTargetType(pauseType, context);
  const targetPause = targets[targetType];

  const optimization = pauseDuration - targetPause;
  const optimizationPercent = (optimization / pauseDuration) * 100;

  if (optimizationPercent < MIN_OPTIMIZATION_PERCENT) {
    return null;
  }

  const recommendedValue = Math.max(0.1, Math.min(targetPause, pauseDuration - 0.01));
  return roundToNDecimalPlaces(recommendedValue, 2);
}

/**
 * Detects pauses between words across all paragraphs, classifies them by
 * type and linguistic context, then attaches the results to each paragraph.
 *
 * Single-pass: collects word-to-word cadence gaps (for speaker pace) and
 * detects pauses in one iteration. Also pre-categorizes entries into
 * `inlinePausesByWordIndex` (Map) and `deadSpacePauseEntries` (array) so
 * consumers don't need to re-iterate in the render path.
 */
export function addPauseEntriesToParagraphs(paragraphs: TranscriptParagraph[]): TranscriptParagraph[] {
  const visibleWords: Array<{ paragraphIndex: number; wordIndex: number; word: Word; segmentId: string }> = [];
  const seenSegments = new Set<string>();

  paragraphs.forEach((paragraph, paragraphIndex) => {
    if (
      paragraph.segmentStartTime !== undefined &&
      paragraph.words?.length > 0 &&
      !seenSegments.has(paragraph.segmentId)
    ) {
      seenSegments.add(paragraph.segmentId);
      visibleWords.push({
        paragraphIndex,
        wordIndex: -1,
        word: { start_time: paragraph.segmentStartTime, end_time: paragraph.segmentStartTime } as Word,
        segmentId: paragraph.segmentId
      });
    }
    paragraph.words?.forEach((word, wordIndex) => {
      visibleWords.push({ paragraphIndex, wordIndex, word, segmentId: paragraph.segmentId });
    });
  });

  // Single pass: collect cadence gaps AND raw pause data simultaneously
  const cadenceGaps: number[] = [];
  const rawPauses: Array<{
    paragraphIndex: number;
    wordEntry: (typeof visibleWords)[0];
    nextWordEntry: (typeof visibleWords)[0];
    pauseDuration: number;
    pauseType: TranscriptPauseType;
  }> = [];

  for (let i = 0; i < visibleWords.length - 1; i++) {
    const current = visibleWords[i];
    const next = visibleWords[i + 1];
    const gap = next.word.start_time - current.word.end_time;

    // Collect cadence gaps (same paragraph, small gaps only)
    if (current.paragraphIndex === next.paragraphIndex && gap > 0 && gap < SPEAKER_PACE_ANALYSIS.maxCadenceGap) {
      cadenceGaps.push(gap);
    }

    // Detect pauses
    const pauseType = getPauseType(gap);
    if (pauseType) {
      rawPauses.push({
        paragraphIndex: current.paragraphIndex,
        wordEntry: current,
        nextWordEntry: next,
        pauseDuration: gap,
        pauseType
      });
    }
  }

  // Determine speaker pace from collected cadence gaps
  let speakerPace: SpeakerPace = 'normal';
  if (cadenceGaps.length > 0) {
    const avgGap = cadenceGaps.reduce((a, b) => a + b, 0) / cadenceGaps.length;
    if (avgGap > SPEAKER_PACE_ANALYSIS.slowAvgGapThreshold) {
      speakerPace = 'slow';
    } else if (avgGap < SPEAKER_PACE_ANALYSIS.fastAvgGapThreshold) {
      speakerPace = 'fast';
    }
  }

  // Build pause entries per paragraph and pre-categorize
  const pauseEntriesByParagraph: Record<number, TranscriptPauseEntry[]> = {};
  const inlineByParagraph: Record<number, Map<number, TranscriptPauseEntry>> = {};
  const deadSpaceByParagraph: Record<number, TranscriptPauseEntry[]> = {};

  for (const raw of rawPauses) {
    const currentWordContent = raw.wordEntry.word.content || '';
    const pauseContext: PauseContext = {
      isParagraphBoundary: raw.wordEntry.paragraphIndex !== raw.nextWordEntry.paragraphIndex,
      hasSentenceEndingPunctuation: /[.!?]["')\]]*$/.test(currentWordContent),
      hasClauseEndingPunctuation: /[,;:]["')\]]*$/.test(currentWordContent)
    };

    const recommendedPause = getRecommendedPauseDuration(raw.pauseDuration, raw.pauseType, speakerPace, pauseContext);
    if (recommendedPause === null) {
      continue;
    }

    const pauseEntry: TranscriptPauseEntry = {
      segmentId: raw.wordEntry.segmentId,
      afterWordIndex: raw.wordEntry.wordIndex,
      startTime: raw.wordEntry.word.end_time,
      endTime: raw.nextWordEntry.word.start_time,
      pauseDuration: raw.pauseDuration,
      pauseType: raw.pauseType,
      recommendedPause,
      label: `${roundToNDecimalPlaces(raw.pauseDuration, 2)}s`,
      isParagraphBoundary: pauseContext.isParagraphBoundary
    };

    const pIdx = raw.paragraphIndex;
    if (!pauseEntriesByParagraph[pIdx]) {
      pauseEntriesByParagraph[pIdx] = [];
    }
    pauseEntriesByParagraph[pIdx].push(pauseEntry);

    // Pre-categorize: dead space paragraph boundaries go into deadSpacePauseEntries, rest into inline map
    if (pauseEntry.pauseType === TranscriptPauseType.DeadSpace && pauseEntry.isParagraphBoundary) {
      if (!deadSpaceByParagraph[pIdx]) {
        deadSpaceByParagraph[pIdx] = [];
      }
      deadSpaceByParagraph[pIdx].push(pauseEntry);
    } else {
      if (!inlineByParagraph[pIdx]) {
        inlineByParagraph[pIdx] = new Map();
      }
      inlineByParagraph[pIdx].set(pauseEntry.afterWordIndex, pauseEntry);
    }
  }

  return paragraphs.map((paragraph, paragraphIndex) => ({
    ...paragraph,
    pauseEntries: pauseEntriesByParagraph[paragraphIndex] || [],
    inlinePausesByWordIndex: inlineByParagraph[paragraphIndex] || new Map(),
    deadSpacePauseEntries: deadSpaceByParagraph[paragraphIndex] || []
  }));
}

export function getPauseOptimizationDeleteId(entry: TranscriptPauseEntry): string {
  return `pause_opt_${addDecimalIfNeeded(entry.startTime)}_${addDecimalIfNeeded(entry.endTime)}`;
}

export function getPauseDeleteBounds(entry: TranscriptPauseEntry): [number, number] {
  const deleteAmount = entry.pauseDuration - entry.recommendedPause;
  const preserveBefore = entry.recommendedPause * PAUSE_PRESERVE_BEFORE_RATIO;
  const deleteStart = entry.startTime + preserveBefore;
  const deleteEnd = deleteStart + deleteAmount;
  return [roundToNDecimalPlaces(deleteStart, 3), roundToNDecimalPlaces(deleteEnd, 3)];
}

export function hasDeleteAtPause(entry: TranscriptPauseEntry, clipDeletes: ClipDeletes | undefined): boolean {
  if (!clipDeletes) {
    return false;
  }
  const [deleteStart, deleteEnd] = getPauseDeleteBounds(entry);
  // A pause counts as optimized when its own PAUSE_OPTIMIZATION delete fully covers the recommended
  // pause delete bounds. Manual/other deletes that cover the pause are handled by
  // isPauseInvalidForOptimization, which hides the pill instead of marking it optimized.
  return Object.values(clipDeletes).some(
    del =>
      del.deleteType === 'PAUSE_OPTIMIZATION' &&
      approxGTE(deleteStart, del.bounds[0]) &&
      approxLTE(deleteEnd, del.bounds[1])
  );
}

export function checkPauseHasOptimization(entry: TranscriptPauseEntry, clipDeletes: ClipDeletes | undefined): boolean {
  return hasDeleteAtPause(entry, clipDeletes);
}

export function isPauseInvalidForOptimization(
  entry: TranscriptPauseEntry,
  clipDeletes: ClipDeletes | undefined
): boolean {
  if (!clipDeletes) {
    return false;
  }

  const [deleteStart, deleteEnd] = getPauseDeleteBounds(entry);

  // Hide the pill when a non-pause delete already fully covers the recommended pause delete range.
  // This catches both a manual delete spanning into the surrounding words and one that covers
  // only the optimized delete length itself.
  return Object.values(clipDeletes).some(del => {
    if (del.deleteType === 'PAUSE_OPTIMIZATION') {
      return false;
    }

    return approxLTE(del.bounds[0], deleteStart) && approxGTE(del.bounds[1], deleteEnd);
  });
}

export function findPauseDeleteKey(entry: TranscriptPauseEntry, clipDeletes: ClipDeletes | undefined): string | null {
  if (!clipDeletes) {
    return null;
  }
  const [deleteStart, deleteEnd] = getPauseDeleteBounds(entry);
  const matchesTimeBounds = (key: string, del: ClipDeletes[string]): boolean =>
    approxEquals(parseFloat(key), deleteStart, 0.01) ||
    (approxGTE(deleteStart, del.bounds[0]) &&
      approxLTE(deleteStart, del.bounds[1]) &&
      approxGTE(deleteEnd, del.bounds[0]) &&
      approxLTE(deleteEnd, del.bounds[1]));

  const pauseOptKey = Object.keys(clipDeletes).find(key => {
    const del = clipDeletes[key];
    return del.deleteType === 'PAUSE_OPTIMIZATION' && matchesTimeBounds(key, del);
  });
  if (pauseOptKey) {
    return pauseOptKey;
  }

  const deleteKey = Object.keys(clipDeletes).find(key => matchesTimeBounds(key, clipDeletes[key]));
  return deleteKey ?? null;
}

export function buildPauseDeletesMap(entries: TranscriptPauseEntry[]): ClipDeletes {
  const deletes: ClipDeletes = {};
  for (const entry of entries) {
    const [deleteStart, deleteEnd] = getPauseDeleteBounds(entry);
    if (deletes[deleteStart]) {
      console.warn(
        '[Pause Optimize] Duplicate pause delete start detected; preserving first entry',
        deleteStart,
        entry.segmentId
      );
      continue;
    }
    deletes[deleteStart] = {
      bounds: [deleteStart, deleteEnd],
      prev_end_time: deleteStart,
      next_start_time: deleteEnd,
      deleteType: 'PAUSE_OPTIMIZATION'
    };
  }
  return deletes;
}
